import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import Landing from '@/pages/Landing';
import Admin from '@/pages/Admin';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import OrderSuccess from '@/pages/OrderSuccess';
import OrderCancelled from '@/pages/OrderCancelled';
import Account from '@/pages/Account';
import OrderForm from '@/pages/OrderForm';
import FastOrder from '@/pages/FastOrder';
import Wholesale from '@/pages/Wholesale';
import WholesaleApply from '@/pages/WholesaleApply';
import WholesaleFAQ from '@/pages/WholesaleFAQ';
import RecipesIndex from '@/pages/RecipesIndex';
import RecipeArticle from '@/pages/RecipeArticle';
import BlogIndex from '@/pages/BlogIndex';
import BlogArticle from '@/pages/BlogArticle';
import ExportPage from '@/pages/Export';
import { AuthProvider } from '@/context/AuthContext';
import { QuoteProvider } from '@/context/QuoteContext';
import { CartProvider } from '@/context/CartContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ScrollToTop from '@/components/ScrollToTop';
import QuoteFab from '@/components/quote/QuoteFab';
import QuoteDrawer from '@/components/quote/QuoteDrawer';
import CartFab from '@/components/cart/CartFab';
import CartDrawer from '@/components/cart/CartDrawer';

const TOAST_OPTIONS = {
  style: {
    background: '#121212',
    color: '#F5F5F0',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 0,
    fontFamily: 'Manrope, sans-serif',
    letterSpacing: '0.02em',
  },
};

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  const showQuoteUi =
    location.pathname === '/' ||
    location.pathname === '/order' ||
    location.pathname === '/account' ||
    location.pathname.startsWith('/fast-order/');
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/order" element={<OrderForm />} />
        <Route path="/wholesale" element={<Wholesale />} />
        <Route path="/wholesale/apply" element={<WholesaleApply />} />
        <Route path="/wholesale-faq" element={<WholesaleFAQ />} />
        <Route path="/recipes" element={<RecipesIndex />} />
        <Route path="/recipes/:slug" element={<RecipeArticle />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/export" element={<ExportPage />} />
        <Route path="/order/success" element={<OrderSuccess />} />
        <Route path="/order/cancelled" element={<OrderCancelled />} />
        <Route path="/account" element={<Account />} />
        <Route path="/fast-order/:token" element={<FastOrder />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showQuoteUi && (
        <>
          <CartFab />
          <CartDrawer />
          <QuoteFab />
          <QuoteDrawer />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <QuoteProvider>
              <Toaster position="bottom-right" theme="dark" toastOptions={TOAST_OPTIONS} />
              <AppRoutes />
            </QuoteProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
